/**
 * An error indicating that get or set of editor state failed because it has not yet been
 * initialized.
 */
export class PmEditorStateNotLoadedError extends Error {
	public readonly tag = "prosemirror-editor-state-not-loaded-error" as const;
	constructor() {
		super("Get or set of editor state failed because it has not yet been initialized");
		this.name = "PmEditorStateNotInitializedError";
	}
}

/**
 * An error indicating that the editor is misconfigured.
 *
 * The main use for this is to indicate that the editor is not configured to write to either collab
 * or disk, which is a very good hint that the user is about to lose all their writes.
 */
export class PmMisconfiguredError extends Error {
	public readonly tag = "prosemirror-misconfigured-error" as const;
	constructor(message: string) {
		super(message);
		this.name = "PmMisconfiguredError";
	}
}

/**
 * An error indicating that we failed to process a transaction because the editor is halted.
 */
export class PmTransactionProcHaltedError extends Error {
	public readonly tag = "prosemirror-transaction-proc-halted-error" as const;
	constructor(error: unknown) {
		const msg = error instanceof Error ? error.message : "unknown reason";
		super(`Failed to process transaction because editor is halted: ${msg}`);
		this.name = "TransactionProcHaltedError";
	}
}

/**
 * An error indicating that we failed to persist the editor state to disk.
 */
export class PmPersistError extends Error {
	public readonly tag = "prosemirror-persist-error" as const;
	constructor(error: unknown) {
		const msg = error instanceof Error ? error.message : "unknown reason";
		super(`Failed to persist editor state to disk: ${msg}`);
		this.name = "PmPersistError";
	}
}

/**
 * An error indicating that our subscription to the collaboration server has terminated
 * unexpectedly.
 */
export class PmCollabSubscriptionFailureError extends Error {
	public readonly tag = "prosemirror-collab-subscription-failure-error" as const;
	constructor(error: unknown) {
		const msg =
			error instanceof Error ? error.message : "Subscription failed for unknown reason";
		super(`Subscription failed unexpectedly: ${msg}`);
		this.name = "PmCollabSubscriptionFailureError";
	}
}

/**
 * An error indicating that we failed to load the editor state from the Reboot collab server.
 */
export class PmRebootLoadError extends Error {
	public readonly tag = "prosemirror-reboot-load-error" as const;
	constructor(error: unknown) {
		const msg =
			typeof error === "string"
				? error
				: error instanceof Error
					? error.message
					: "unknown reason";
		super(`Failed to load editor state from Reboot: ${msg}`);
		this.name = "PmRebootLoadError";
	}
}

/**
 * An error indicating that we failed to load the editor state from disk.
 */
export class PmDiskLoadError extends Error {
	public readonly tag = "prosemirror-disk-load-error" as const;
	constructor(error: unknown) {
		const msg =
			typeof error === "string"
				? error
				: error instanceof Error
					? error.message
					: "unknown reason";
		super(`Failed to load editor state from disk: ${msg}`);
		this.name = "PmDiskLoadError";
	}
}

/**
 * An error indicating that we failed to load the editor state from the remote DocsAPI.
 */
export class PmRemoteLoadError extends Error {
	public readonly tag = "prosemirror-remote-load-error" as const;
	constructor(error: unknown) {
		const msg =
			typeof error === "string"
				? error
				: error instanceof Error
					? error.message
					: "unknown reason";
		super(`Failed to load editor state from remote: ${msg}`);
		this.name = "PmRemoteLoadError";
	}
}
